import { PERMISSION_POLICY } from "permissions/policy";

export class PermissionService {
  constructor(user, permissions = {}, coordinatorResolver = null) {
    this.user = user;
    this.permissions = permissions;
    this.coordinatorResolver = coordinatorResolver;
  }

  isAuthenticated() {
    return this.user?.isAuthenticated === true;
  }

  isSelfEditing(targetUserId) {
    return this.user?.id === targetUserId;
  }

  hasKeyPermission(collection, key, action = "read") {
    return this.permissions[collection]?.keys?.[key]?.[action] === true;
  }

  evaluatePermission(condition) {
    if (!condition) return true;

    if (typeof condition === "string") {
      switch (condition) {
        case "isAuthenticated":
          return this.isAuthenticated();
        case "isSelfEditing":
          return this.isSelfEditing();
        default:
          return false;
      }
    }

    if (typeof condition === "object") {
      const {
        type,
        collection,
        key,
        access,
        action = "read",
        operator = "AND",
      } = condition;

      switch (type) {
        case "collection":
          return this.hasCollectionPermission(collection, action, access);
        case "key":
          return this.hasFieldAccess(collection, action, key, access);
        case "multiple":
          return this.evaluateMultipleConditions(
            condition.conditions,
            operator,
          );
        case "fullAccess":
          return this.hasFullAccess(collection, action);
        case "role":
          return this.hasRole(condition.role);
        default:
          return false;
      }
    }

    if (Array.isArray(condition)) {
      return this.evaluateMultipleConditions(condition);
    }

    return false;
  }

  evaluateMultipleConditions(conditions, operator = "AND") {
    if (operator === "AND") {
      return conditions.every((cond) => this.evaluatePermission(cond));
    } else if (operator === "OR") {
      return conditions.some((cond) => this.evaluatePermission(cond));
    }
    return false;
  }

  hasCollectionPermission(collection, action = "read", access) {
    const collectionPermissions = this.permissions[collection];
    if (!collectionPermissions) return false;

    const actionPermissions = collectionPermissions[action];
    if (!actionPermissions) return false;

    if (access && actionPermissions.access !== access) {
      return false;
    }

    return (
      actionPermissions.access === "full" ||
      actionPermissions.access === "partial"
    );
  }

  hasFieldAccess(collection, action = "read", field = "*", access) {
    const collectionPermissions = this.permissions[collection];
    if (!collectionPermissions) return false;

    const actionPermissions = collectionPermissions[action];
    if (!actionPermissions) return false;

    if (
      actionPermissions.access === "none" ||
      (access && actionPermissions.access !== access)
    ) {
      return false;
    }

    const fields = actionPermissions.fields || [];

    return fields.includes(field) || fields.includes("*");
  }

  hasRole(role) {
    return this.user.role?.name === role;
  }

  getAccessibleFields(collection, action = "read") {
    const collectionPermissions = this.permissions[collection];
    if (!collectionPermissions) return [];

    const actionPermissions = collectionPermissions[action];
    if (!actionPermissions) return [];

    if (
      actionPermissions.access !== "full" &&
      actionPermissions.access !== true
    ) {
      return [];
    }

    return actionPermissions.fields || [];
  }

  hasFullAccess(collection, action = "read") {
    const collectionPermissions = this.permissions[collection];
    if (!collectionPermissions) return false;

    const actionPermissions = collectionPermissions[action];
    if (!actionPermissions) return false;

    return (
      actionPermissions.access === "full" &&
      (actionPermissions.fields?.includes("*") || false)
    );
  }

  // ===========================================================================
  // ENHANCED PERMISSION METHODS
  // ===========================================================================

  isCoordinatorOf(coordinatorIds = []) {
    return coordinatorIds.includes(this.user?.id);
  }

  async can(action, collection, options = {}) {
    const ctx = this.context ?? {};

    const {
      field = "*",
      coordinatorIds = [],
      access,
      projectSlug = ctx.projectSlug,
      coordinatorTypes = [],
      jobId = ctx.jobId,
      // New identifiers for entity-first resolution
      billableId = ctx.billableId,
      milestoneId = ctx.milestoneId,
      projectId = ctx.projectId,
      casestudyId = ctx.casestudyId,
    } = options;

    const collectionPermissions = this.permissions[collection];
    if (!collectionPermissions) return false;

    const actionPermissions = collectionPermissions[action];
    if (!actionPermissions || actionPermissions?.access === "none")
      return false;

    if (actionPermissions?.access === "full") return true;

    const policy = PERMISSION_POLICY[collection]?.[action];

    if (!policy) {
      return false; // action not allowed
    }

    const { requiresCoordinator } = policy;

    if (field !== "*") {
      const fields = actionPermissions.fields || [];
      if (
        actionPermissions.access === "partial" &&
        !fields.includes(field) &&
        !fields.includes("*")
      ) {
        return false;
      }
      if (!fields.includes(field) && !fields.includes("*")) {
        return false;
      }
      if (!requiresCoordinator) {
        return true;
      }
    }

    // If no coordinator required, just return permission-based access
    if (!requiresCoordinator) {
      return (
        actionPermissions.access === "full" ||
        actionPermissions.access === "partial"
      );
    }

    let resolvedCoordinatorIds = [...coordinatorIds];

    if (coordinatorTypes.length > 0 && this.coordinatorResolver) {
      for (const type of coordinatorTypes) {
        let identifier = null;

        switch (type) {
          case "PC":
          case "SC":
          case "AM":
            identifier = {
              billableId,
              milestoneId,
              projectId,
              projectSlug,
              jobId,
              casestudyId,
            };
            break;
          case "JC":
            identifier = {
              billableId,
              milestoneId,
              projectSlug,
              jobId,
              casestudyId,
            };
            break;

          case "MC":
            identifier = {
              milestoneId,
              jobId,
              casestudyId,
            };
            break;
        }

        if (identifier) {
          const coordinatorId =
            await this.coordinatorResolver.resolveCoordinator(type, identifier);

          if (coordinatorId) {
            if (Array.isArray(coordinatorId) && coordinatorId.length > 0) {
              resolvedCoordinatorIds.push(...coordinatorId);
            } else {
              resolvedCoordinatorIds.push(coordinatorId);
            }
          }
        }
      }
    }

    if (access && actionPermissions.access !== access) {
      return false;
    }

    // if (
    //   resolvedCoordinatorIds.length > 0 &&
    //   !this.isCoordinatorOf(resolvedCoordinatorIds)
    // ) {
    //   return false;
    // }
    if (requiresCoordinator && resolvedCoordinatorIds.length === 0) {
      return false;
    }


    if (requiresCoordinator && !this.isCoordinatorOf(resolvedCoordinatorIds)) {
      return false;
    }

    return (
      actionPermissions.access === "full" ||
      actionPermissions.access === "partial"
    );
  }

  async canRead(collection, options = {}) {
    return this.can("read", collection, options);
  }

  async canCreate(collection, options = {}) {
    return this.can("create", collection, options);
  }

  async canUpdate(collection, options = {}) {
    return this.can("update", collection, options);
  }

  async canDelete(collection, options = {}) {
    return this.can("delete", collection, options);
  }

  parsePermissionRule(rule) {
    const [action, coordinatorsStr] = rule.split(":");
    const coordinatorTypes =
      coordinatorsStr === "*"
        ? []
        : coordinatorsStr.split(",").map((s) => s.trim());

    const denyAll = coordinatorTypes.includes("none");
    const allowAdmin = coordinatorTypes.includes("Admin");

    return {
      action,
      coordinatorTypes: coordinatorTypes.filter(
        (c) => c !== "none" && c !== "Admin",
      ),
      denyAll,
      allowAdmin,
    };
  }

  async canByRule(rule, collection, options = {}) {
    const parsed = this.parsePermissionRule(rule);

    if (parsed.denyAll) {
      return false;
    }

    return this.can(parsed.action, collection, {
      ...options,
      coordinatorTypes: parsed.coordinatorTypes,
    });
  }

  async canByRules(rules, collection, options = {}) {
    const results = {};

    for (const rule of rules) {
      const parsed = this.parsePermissionRule(rule);

      if (parsed.denyAll) {
        return false;
      }

      results[parsed.action] = await this.can(parsed.action, collection, {
        ...options,
        coordinatorTypes: parsed.coordinatorTypes,
      });
    }

    return results;
  }
}
